import { Link, useNavigate } from "react-router-dom"
import { BookOpen, Play, Clock, Star, CheckCircle, TrendingUp, Award, ArrowRight, FileText, Calendar, Users, BarChart3 } from "lucide-react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { formatPrice, formatDuration } from "../utils/formatters"
import { useCourseProgress } from "../hooks/useCourseProgress"
import { ProfileProgressSkeleton } from "./skeletons"

export default function MyEnrolledCourses() {
  const navigate = useNavigate()
  
  // Récupérer la progression de tous les cours depuis l'API
  const { progress: courseProgress, loading: progressLoading, error: progressError } = useCourseProgress()
  
  // Transformer les données de l'API pour correspondre au format attendu
  const courses = courseProgress?.map(progress => {
    const course = progress.course
    return {
      id: course.id,
      title: course.title,
      slug: course.slug,
      thumbnail: course.thumbnail,
      banner: course.banner,
      category: course.category,
      level: course.level,
      language: course.language,
      rating: course.rating || 4.5,
      duration: course.duration || 0,
      price: course.price,
      currency: course.currency || "XOF",
      enrollmentStatus: progress.completedAt ? "COMPLETED" : (progress.progress > 0 ? "IN_PROGRESS" : "ENROLLED"),
      progress: progress.progress || 0,
      completedLessons: progress.completedLessons || 0,
      totalLessons: progress.totalLessons || 0,
      enrolledAt: progress.createdAt,
      lastAccessedAt: progress.lastAccessedAt,
      resumeBlock: progress.resumeBlock,
      nextBlock: progress.nextBlock,
      lastAccessedBlock: progress.lastAccessedBlock,
      lastAccessedModule: progress.lastAccessedModule,
      modules: progress.modules || []
    }
  }) || []
  
  // Données mockées statiques (fallback si l'API ne retourne rien)
  const mockCourses = [
    {
      id: "1",
      title: "Introduction à React",
      slug: "introduction-react",
      thumbnail: "https://via.placeholder.com/300x200",
      category: { name: "Développement Web" },
      rating: 4.5,
      duration: 36000, // 10 heures en secondes
      price: 50000,
      currency: "XOF",
      enrollmentStatus: "COMPLETED",
      progress: 100,
      enrolledAt: "2024-01-10T00:00:00.000Z"
    },
    {
      id: "2",
      title: "JavaScript Avancé",
      slug: "javascript-avance",
      thumbnail: "https://via.placeholder.com/300x200",
      category: { name: "Développement Web" },
      rating: 4.7,
      duration: 54000, // 15 heures en secondes
      price: 75000,
      currency: "XOF",
      enrollmentStatus: "IN_PROGRESS",
      progress: 65,
      enrolledAt: "2024-01-15T00:00:00.000Z"
    },
    {
      id: "3",
      title: "Node.js pour débutants",
      slug: "nodejs-debutants",
      thumbnail: "https://via.placeholder.com/300x200",
      category: { name: "Backend" },
      rating: 4.3,
      duration: 43200, // 12 heures en secondes
      price: 60000,
      currency: "XOF",
      enrollmentStatus: "IN_PROGRESS",
      progress: 30,
      enrolledAt: "2024-01-20T00:00:00.000Z"
    },
    {
      id: "4",
      title: "TypeScript Essentiel",
      slug: "typescript-essentiel",
      thumbnail: "https://via.placeholder.com/300x200",
      category: { name: "Développement Web" },
      rating: 4.8,
      duration: 28800, // 8 heures en secondes
      price: 55000,
      currency: "XOF",
      enrollmentStatus: "ENROLLED",
      progress: 0,
      enrolledAt: "2024-01-25T00:00:00.000Z"
    }
  ]
  
  // Organiser les cours par statut
  const completedCourses = courses.filter(course => course.enrollmentStatus === "COMPLETED")
  const inProgressCourses = courses.filter(course => course.enrollmentStatus === "IN_PROGRESS")
  const enrolledCourses = courses.filter(course => course.enrollmentStatus === "ENROLLED")
  
  const coursesLoading = progressLoading
  const coursesError = progressError
  
  const handleViewCertificate = () => {
    // Rediriger vers l'onglet Certificats dans le profil
    navigate('/profile?tab=achievements')
  }

  const getCourseUrl = (course) => {
    // Priorité 1: Si un resumeBlock est disponible, rediriger vers celui-ci (reprendre où on s'est arrêté)
    // C'est le bloc le plus pertinent pour continuer la progression
    if (course.resumeBlock && course.resumeBlock.id && course.resumeBlock.moduleId) {
      const block = course.resumeBlock
      if (block.type === 'QUIZ') {
        // Si c'est un quiz, rediriger vers la page du quiz avec le bon format d'URL
        return `/courses/${course.id}/modules/${block.moduleId}/quiz`
      } else {
        // Rediriger vers le bloc spécifique
        return `/courses/${course.id}/modules/${block.moduleId}/blocks/${block.id}`
      }
    }
    
    // Priorité 2: Si un nextBlock est disponible, rediriger vers celui-ci (prochain bloc à faire)
    if (course.nextBlock && course.nextBlock.id && course.nextBlock.moduleId) {
      const block = course.nextBlock
      if (block.type === 'QUIZ') {
        return `/courses/${course.id}/modules/${block.moduleId}/quiz`
      } else {
        return `/courses/${course.id}/modules/${block.moduleId}/blocks/${block.id}`
      }
    }
    
    // Priorité 3: Si un lastAccessedBlock est disponible, rediriger vers celui-ci
    if (course.lastAccessedBlock && course.lastAccessedBlock.id && course.lastAccessedBlock.moduleId) {
      const block = course.lastAccessedBlock
      if (block.type === 'QUIZ') {
        return `/courses/${course.id}/modules/${block.moduleId}/quiz`
      } else {
        return `/courses/${course.id}/modules/${block.moduleId}/blocks/${block.id}`
      }
    }
    
    // Priorité 4: Si un lastAccessedModule est disponible, rediriger vers ce module
    if (course.lastAccessedModule && course.lastAccessedModule.id) {
      return `/courses/${course.id}/modules/${course.lastAccessedModule.id}`
    }
    
    // Priorité 5: Si des modules existent, rediriger vers le premier module (par ordre)
    if (course.modules && course.modules.length > 0) {
      // Trier les modules par ordre et prendre le premier
      const sortedModules = [...course.modules].sort((a, b) => (a.order || 0) - (b.order || 0))
      const firstModule = sortedModules[0]
      if (firstModule && firstModule.id) {
        return `/courses/${course.id}/modules/${firstModule.id}`
      }
    }
    
    // Dernière option: rediriger vers la page du cours (vue d'ensemble)
    if (course.slug) {
      return `/courses/${course.slug}`
    }
    return `/courses/${course.id}`
  }
  
  // Fonction pour obtenir le texte du bouton selon la progression
  const getButtonText = (course) => {
    if (course.enrollmentStatus === "COMPLETED") {
      return "Voir la certification"
    }
    
    // Si on a un resumeBlock ou nextBlock, on continue
    if (course.resumeBlock || course.nextBlock || course.lastAccessedBlock) {
      return "Continuer"
    }
    
    // Sinon, on commence
    return "Commencer"
  }
  
  // Fonction pour obtenir l'icône du bouton selon la progression
  const getButtonIcon = (course) => {
    if (course.enrollmentStatus === "COMPLETED") {
      return Award
    }
    
    // Si on a un resumeBlock ou nextBlock, on continue
    if (course.resumeBlock || course.nextBlock || course.lastAccessedBlock) {
      return Play
    }
    
    // Sinon, on commence
    return ArrowRight
  }

  const getEnrollmentStatusBadge = (status) => {
    switch (status) {
      case "COMPLETED":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Terminé
          </Badge>
        )
      case "IN_PROGRESS":
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">
            <TrendingUp className="w-3 h-3 mr-1" />
            En cours
          </Badge>
        )
      case "ENROLLED":
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200">
            <Play className="w-3 h-3 mr-1" />
            Inscrit
          </Badge>
        )
      default:
        return null
    }
  }

  if (coursesLoading) {
    return <ProfileProgressSkeleton />
  }

  if (coursesError) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <p className="text-sm sm:text-base text-red-600">Erreur lors du chargement des cours</p>
      </div>
    )
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
          Aucun cours acheté
        </h3>
        <p className="text-sm sm:text-base text-gray-600 mb-4">
          Vous n'avez pas encore acheté de cours.
        </p>
        <Link to="/courses">
          <Button className="bg-[#141835] hover:bg-[#1a1f4a] text-sm sm:text-base w-full sm:w-auto">
            <BookOpen className="w-4 h-4 mr-2" />
            Découvrir les cours
          </Button>
        </Link>
      </div>
    )
  }

  // Fonction pour rendre un cours en ligne
  const renderCourseRow = (course, actionButton) => (
    <div 
      className="flex flex-col sm:flex-row gap-4 sm:gap-6 p-4 sm:p-6 border border-gray-200 rounded-xl hover:shadow-lg transition-all bg-white group overflow-hidden"
    >
      {/* Thumbnail - Responsive avec différentes tailles */}
      <div className="relative flex-shrink-0 w-full sm:w-32 h-48 sm:h-32">
        <div className="relative w-full h-full rounded-xl overflow-hidden shadow-md">
          <img
            src={course.thumbnail || course.banner || "/placeholder.svg"}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="absolute top-2 left-2 sm:-top-2 sm:-left-2 z-10">
          {getEnrollmentStatusBadge(course.enrollmentStatus)}
        </div>
      </div>

      {/* Informations du cours */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        {/* En-tête avec titre et métadonnées */}
        <div className="mb-4">
          <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-2 line-clamp-2 group-hover:text-[#141835] transition-colors">
            {course.title}
          </h3>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 mb-3">
            <Badge variant="outline" className="text-xs font-medium">
              {course.category?.translatedName || course.category?.name || "Catégorie"}
            </Badge>
            {course.level && (
              <Badge variant="outline" className="text-xs">
                {course.level === "BEGINNER" ? "Débutant" : 
                 course.level === "INTERMEDIATE" ? "Intermédiaire" : 
                 course.level === "ADVANCED" ? "Avancé" : course.level}
              </Badge>
            )}
            <div className="flex items-center space-x-1">
              <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-xs sm:text-sm font-medium">{course.rating?.toFixed(1) || "4.5"}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm">{formatDuration(course.duration || 0, 'seconds')}</span>
            </div>
            {course.language && (
              <div className="flex items-center space-x-1">
                <span className="text-xs">🌐</span>
                <span className="text-xs sm:text-sm">{course.language}</span>
              </div>
            )}
          </div>

          {/* Statistiques de progression - Responsive grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-xs text-gray-600 mb-1">
                <BarChart3 className="w-3 h-3 hidden sm:block" />
                <span className="text-[10px] sm:text-xs">Progression</span>
              </div>
              <div className="text-base sm:text-lg font-bold text-[#141835]">
                {course.progress?.toFixed(0) || 0}%
              </div>
            </div>

            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 text-xs text-gray-600 mb-1">
                <BookOpen className="w-3 h-3 hidden sm:block" />
                <span className="text-[10px] sm:text-xs">Modules</span>
              </div>
              <div className="text-base sm:text-lg font-bold text-gray-900">
                {course.modules?.length || 0}
              </div>
            </div>
          </div>

          {/* Barre de progression principale */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
              <span className="text-gray-600 font-medium">Progression globale</span>
              <span className="font-bold text-[#141835]">{course.progress?.toFixed(0) || 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#141835] to-[#1a1f4a] h-2 sm:h-3 rounded-full transition-all duration-500 shadow-sm"
                style={{ width: `${course.progress || 0}%` }}
              />
            </div>
          </div>

          {/* Dernier accès et bloc actuel - Responsive stack */}
          {course.lastAccessedAt && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 flex-shrink-0" />
                <span className="break-words">
                  Dernier accès: {new Date(course.lastAccessedAt).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {course.lastAccessedBlock && (
                <div className="flex items-center gap-1">
                  <Play className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate max-w-full sm:max-w-[200px]">
                    {course.lastAccessedBlock.title}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Informations d'inscription */}
          {course.enrolledAt && (
            <p className="text-xs text-gray-500 mb-3">
              Inscrit le {new Date(course.enrolledAt).toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </p>
          )}
        </div>

        {/* Ligne avec barre de progression et bouton alignés - Responsive */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-4 border-t border-gray-100">
          {/* Bouton d'action - Full width sur mobile */}
          <div className="flex-shrink-0 w-full sm:w-auto">
            <div className="w-full sm:w-auto">
              {actionButton}
            </div>
          </div>
          
          {/* Lien vers le cours */}
          <Link 
            to={`/courses/${course.id}`}
            className="text-center sm:text-left sm:ml-auto text-xs sm:text-sm text-[#141835] hover:text-[#1a1f4a] font-medium flex items-center justify-center sm:justify-start gap-1 transition-colors py-2 sm:py-0"
          >
            <span>Voir les détails</span>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </Link>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Section Cours terminés */}
      {completedCourses.length > 0 && (
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-4">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Cours terminés ({completedCourses.length})
            </h3>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {completedCourses.map((course) => (
              <div key={course.id}>
                {renderCourseRow(
                  course,
                  <Button 
                    className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white whitespace-nowrap flex items-center justify-center text-sm sm:text-base" 
                    onClick={handleViewCertificate}
                  >
                    <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    <span>Voir la certification</span>
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section Cours entamés (en cours) */}
      {inProgressCourses.length > 0 && (
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-4">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Cours entamés ({inProgressCourses.length})
            </h3>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {inProgressCourses.map((course) => {
              const ButtonIcon = getButtonIcon(course)
              const buttonText = getButtonText(course)
              return (
                <div key={course.id}>
                  {renderCourseRow(
                    course,
                    <Button 
                      className="w-full sm:w-auto bg-[#203a8a] hover:bg-[#1a1f4a] text-white whitespace-nowrap flex items-center justify-center text-sm sm:text-base" 
                      asChild
                    >
                      <Link to={getCourseUrl(course)} className="flex items-center justify-center">
                        <ButtonIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                        <span>{buttonText}</span>
                      </Link>
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Section Cours inscrits */}
      {enrolledCourses.length > 0 && (
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-4">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Cours inscrits ({enrolledCourses.length})
            </h3>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {enrolledCourses.map((course) => {
              const ButtonIcon = getButtonIcon(course)
              const buttonText = getButtonText(course)
              return (
                <div key={course.id}>
                  {renderCourseRow(
                    course,
                    <Button 
                      className="w-full sm:w-auto bg-[#203a8a] hover:bg-[#1a1f4a] text-white whitespace-nowrap flex items-center justify-center text-sm sm:text-base" 
                      asChild
                    >
                      <Link to={getCourseUrl(course)} className="flex items-center justify-center">
                        <ButtonIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                        <span>{buttonText}</span>
                      </Link>
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

